import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { api } from "../lib/api";
import {
  ScanLine, Check, AlertTriangle, X, Camera, Loader2, ChevronRight,
  Car, Users, Clock,
} from "lucide-react";
import { Navbar } from "../components/Navbar";
import { toast } from "sonner";

const SCAN_REGION_ID = "rb-qr-reader";

export default function Scanner() {
  const [status, setStatus] = useState("idle"); // idle | running | error
  const [lastResult, setLastResult] = useState(null); // CheckInResponse or {error}
  const [busy, setBusy] = useState(false);
  const scannerRef = useRef(null);
  const lastScanRef = useRef({ code: null, at: 0 });
  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem("rb100_admin_token")) {
      navigate("/admin/login");
    }
  }, [navigate]);

  const handleScan = async (decodedText) => {
    // De-duplicate (camera fires very fast)
    const now = Date.now();
    if (lastScanRef.current.code === decodedText && now - lastScanRef.current.at < 3500) return;
    lastScanRef.current = { code: decodedText, at: now };
    if (busy) return;

    setBusy(true);
    try {
      const { data } = await api.post("/check-in", { reference_code: decodedText });
      setLastResult({ ok: true, data });
      // beep / visual feedback
      try { navigator.vibrate?.(150); } catch {}
    } catch (err) {
      const msg = err?.response?.data?.detail || "خطأ في القراءة";
      setLastResult({ ok: false, error: msg, raw: decodedText });
      try { navigator.vibrate?.([60, 80, 60]); } catch {}
    } finally {
      setTimeout(() => setBusy(false), 1200);
    }
  };

  const start = async () => {
    setLastResult(null);
    setStatus("starting");
    try {
      const html5QrCode = new Html5Qrcode(SCAN_REGION_ID, { verbose: false });
      scannerRef.current = html5QrCode;
      await html5QrCode.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 260, height: 260 }, aspectRatio: 1.0 },
        handleScan,
        () => {} // ignore parse errors
      );
      setStatus("running");
    } catch (e) {
      console.error(e);
      setStatus("error");
      toast.error("تعذّر تشغيل الكاميرا. تأكّد من السماح بالوصول.");
    }
  };

  const stop = async () => {
    try {
      if (scannerRef.current) {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      }
    } catch (err) {
      // Scanner may already be stopped or component unmounting — log for diagnostics
      console.debug("Scanner stop:", err?.message);
    }
    scannerRef.current = null;
    setStatus("idle");
  };

  useEffect(() => {
    return () => { stop(); }; // eslint-disable-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="scanner-page" className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-2xl mx-auto px-6 py-10">
        <div className="mb-8 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className="h-[2px] w-10 bg-rb-red" />
              <span className="text-xs tracking-[0.4em] text-rb-red font-bold">
                GATE SCANNER
              </span>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-rb-ink">
              تسجيل الحضور
            </h1>
            <p className="text-rb-textMuted text-sm mt-2">
              وجّه الكاميرا نحو الباركود الموجود على الباج.
            </p>
          </div>
          <Link
            to="/admin/dashboard"
            data-testid="scanner-back"
            className="text-sm text-rb-textMuted hover:text-rb-ink flex items-center gap-1 mt-1"
          >
            <ChevronRight className="w-4 h-4" />
            لوحة الإدارة
          </Link>
        </div>

        {/* Camera area */}
        <div className="bg-black border border-rb-border relative aspect-square w-full overflow-hidden">
          <div id={SCAN_REGION_ID} className="absolute inset-0 [&_video]:!w-full [&_video]:!h-full [&_video]:!object-cover" />
          {status !== "running" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center p-8">
              {status === "starting" ? (
                <>
                  <Loader2 className="w-10 h-10 animate-spin mb-4" />
                  <p className="text-sm">جاري تشغيل الكاميرا...</p>
                </>
              ) : (
                <>
                  <ScanLine className="w-14 h-14 mb-5 text-rb-red" />
                  <p className="text-base mb-6 max-w-xs">
                    اضغط لبدء الماسح الضوئي والسماح بالوصول للكاميرا.
                  </p>
                  <button
                    onClick={start}
                    data-testid="scanner-start-btn"
                    className="bg-rb-red hover:bg-rb-redHover text-white font-bold px-8 py-3 transition-colors flex items-center gap-2"
                  >
                    <Camera className="w-4 h-4" />
                    تشغيل الكاميرا
                  </button>
                </>
              )}
            </div>
          )}
          {/* Active overlay */}
          {status === "running" && (
            <div className="pointer-events-none absolute inset-0">
              <div className="absolute inset-0 border-[40px] border-black/40" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[260px] h-[260px] border-2 border-rb-red">
                <div className="absolute -top-1 -right-1 w-6 h-6 border-t-4 border-r-4 border-white" />
                <div className="absolute -top-1 -left-1 w-6 h-6 border-t-4 border-l-4 border-white" />
                <div className="absolute -bottom-1 -right-1 w-6 h-6 border-b-4 border-r-4 border-white" />
                <div className="absolute -bottom-1 -left-1 w-6 h-6 border-b-4 border-l-4 border-white" />
              </div>
            </div>
          )}
        </div>

        {status === "running" && (
          <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 text-xs text-rb-textMuted">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rb-red opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-rb-red" />
              </span>
              الماسح يعمل...
            </div>
            <button
              onClick={stop}
              data-testid="scanner-stop-btn"
              className="text-xs text-rb-textMuted hover:text-rb-ink border border-rb-border px-3 py-1.5"
            >
              إيقاف
            </button>
          </div>
        )}

        {/* Result card */}
        {lastResult && (
          <div className="mt-6">
            <ResultCard result={lastResult} />
          </div>
        )}
      </div>
    </div>
  );
}

const ResultCard = ({ result }) => {
  if (!result.ok) {
    return (
      <div data-testid="scan-result-error" className="bg-red-50 border-2 border-red-300 p-6">
        <div className="flex items-start gap-4">
          <div className="bg-rb-red w-12 h-12 flex items-center justify-center text-white shrink-0">
            <X className="w-7 h-7" strokeWidth={3} />
          </div>
          <div className="flex-1">
            <h3 className="font-heading font-bold text-xl text-red-700 mb-1">رفض الدخول</h3>
            <p className="text-sm text-red-700/90">{result.error}</p>
            {result.raw && (
              <p className="text-xs text-red-700/60 mt-2 font-mono break-all">{result.raw}</p>
            )}
          </div>
        </div>
      </div>
    );
  }
  const d = result.data;
  const fresh = d.just_checked_in;
  return (
    <div
      data-testid={fresh ? "scan-result-success" : "scan-result-already"}
      className={`border-2 p-6 ${fresh ? "bg-green-50 border-green-400" : "bg-amber-50 border-amber-400"}`}
    >
      <div className="flex items-start gap-4">
        <div className={`w-14 h-14 flex items-center justify-center text-white shrink-0 ${fresh ? "bg-green-600" : "bg-amber-500"}`}>
          {fresh ? <Check className="w-8 h-8" strokeWidth={3} /> : <AlertTriangle className="w-7 h-7" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <h3 className={`font-heading font-extrabold text-2xl ${fresh ? "text-green-700" : "text-amber-700"}`}>
              {fresh ? "أهلاً وسهلاً" : "تم الدخول مسبقاً"}
            </h3>
          </div>
          <div className="space-y-2.5 text-sm">
            <Row icon={Users} label="الاسم" value={d.name} />
            <Row icon={Car} label="اللوحة" value={d.plate_number} mono />
            {(d.car_make || d.car_model) && (
              <Row icon={Car} label="السيارة" value={[d.car_make, d.car_model].filter(Boolean).join(" ")} />
            )}
            {!fresh && d.attended_at && (
              <Row icon={Clock} label="سُجّل سابقاً" value={new Date(d.attended_at).toLocaleString("ar")} />
            )}
            {!fresh && d.attended_by && (
              <Row icon={Users} label="بواسطة" value={d.attended_by} />
            )}
            <div className="pt-2 mt-2 border-t border-current/15 text-xs">
              رقم المرجع: <span className="font-mono font-bold">{d.reference_code}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Row = ({ icon: Icon, label, value, mono }) => (
  <div className="flex items-center gap-2">
    <Icon className="w-4 h-4 opacity-70" />
    <span className="text-xs opacity-70 ml-1">{label}:</span>
    <span className={`font-bold flex-1 ${mono ? "font-mono tracking-widest" : ""}`}>{value}</span>
  </div>
);
